// API Configuration — local dev uses same-origin + package.json proxy so httpOnly cookies work
const defaultBackendUrl =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? ''
    : 'https://api.exituity.com'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || defaultBackendUrl
if (process.env.NODE_ENV === 'development') {
  console.log('Backend URL:', BACKEND_URL || '(same-origin /api via proxy)')
}

const API_CONFIG = {
  BASE_URL: BACKEND_URL,
  
  // API endpoints
  ENDPOINTS: {
    HEALTH: '/api/health',
    DOCUMENTS: {
      UPLOAD: '/api/documents/upload',
      DELETE: (id) => `/api/documents/${id}`,
      GET: (id) => `/api/documents/${id}`,
      LIST: '/api/documents'
    },
    SUBSCRIPTIONS: {
      SUBSCRIBE: '/api/subscriptions/subscribe',
      UNSUBSCRIBE: '/api/subscriptions/unsubscribe'
    },
    VALUATIONS: {
      DCF: '/api/valuations/dcf',
      COMPS: '/api/valuations/comps',
      MULTIPLE: '/api/valuations/multiple',
      HEALTH: '/api/valuations/health',
      SUMMARY: '/api/valuations/summary'
    },
    ML_PRICE: {
      PREDICT: '/api/ml-price/predict',
      OPTIONS: '/api/ml-price/options',
    },
    DEVELOPER: {
      KEYS: '/api/developer/keys',
      KEY: (id) => `/api/developer/keys/${id}`,
      USAGE: '/api/developer/usage',
    },
    VECTOR_DB: {
      QUERY: '/api/vector-db/query',
      DOCUMENTS: '/api/vector-db/documents',
      UPLOAD: '/api/vector-db/upload',
      VISUALIZE: '/api/vector-db/visualize',
      STATS: '/api/vector-db/stats'
    },
    CHAT: '/api/chat',
    AUTH: {
      FORGOT_PASSWORD: '/api/auth/forgot-password',
      RESET_PASSWORD: '/api/auth/reset-password'
    },
    CIM_AGENT_CHAT: '/api/cim-agent/chat',
    CIM_AGENT_ANALYZE_REFERENCE: '/api/cim-agent/analyze-reference',
    CIM_AGENT_REWRITE_IN_TONE: '/api/cim-agent/rewrite-in-tone',
    CIM_AGENT_TEMPLATES: '/api/cim-agent/templates',
    CIM_EXTRACT_FROM_NOTES: '/api/cim-onboarding/extract-from-notes',
    CIM_EXTRACT_TEXT_FROM_FILE: '/api/cim-onboarding/extract-text-from-file',
    // Step 1 (SMB-direct): conversational seller onboarding that decides deal-room folders.
    SELLER_ONBOARDING_CHAT: '/api/seller-onboarding/chat',
    SELLER_ONBOARDING_SESSION: '/api/seller-onboarding/session',
    SELLER_ONBOARDING_SESSION_GET: (id) => `/api/seller-onboarding/session/${id}`,
    SELLER_ONBOARDING_COMPLETE: '/api/seller-onboarding/complete',
    // Phase 2: review the deal room (missing folders, mis-filed docs, readiness).
    SELLER_ONBOARDING_REVIEW: '/api/seller-onboarding/review',
    // Phase 3: CIM save (reuses the existing CIM questionnaire persistence).
    CIM_ONBOARDING_SAVE: '/api/cim-onboarding',
    BILLING: {
      CONFIG: '/api/billing/config',
      SETUP_INTENT: '/api/billing/setup-intent',
      PAYMENT_METHODS: '/api/billing/payment-methods',
      ADD_PAYMENT_METHOD: '/api/billing/payment-methods',
      REMOVE_PAYMENT_METHOD: (id) => `/api/billing/payment-methods/${id}`,
      SET_DEFAULT_PAYMENT_METHOD: (id) => `/api/billing/payment-methods/${id}/set-default`,
      INVOICES: '/api/billing/invoices',
      SUBSCRIPTION: '/api/billing/subscription',
      CANCEL_SUBSCRIPTION: '/api/billing/subscription/cancel'
    },
    PAYMENT: {
      CREATE: '/api/payment/',
      PLANS: '/api/payment/plans'
    },
    XTRACT: {
      PACKS: '/api/xtract/packs',
      CREDITS: '/api/xtract/credits',
      CHECKOUT: '/api/xtract/checkout',
      CHECKOUT_SESSION: (id) => `/api/xtract/checkout/${id}`,
      EXTRACT: '/api/xtract/extract',
    },
    REWARDO: {
      ME: '/api/rewardo/me',
      ONBOARDING: '/api/rewardo/onboarding',
      PREFERENCES: '/api/rewardo/preferences',
      CATALOG: '/api/rewardo/catalog',
      CARDS: '/api/rewardo/cards',
      CARD: (id) => `/api/rewardo/cards/${id}`,
      RECOMMEND: '/api/rewardo/recommend',
      MERCHANTS_RESOLVE: '/api/rewardo/merchants/resolve',
      DATA: '/api/rewardo/data',
      PLAID_STATUS: '/api/rewardo/plaid/status',
      PLAID_LINK_TOKEN: '/api/rewardo/plaid/link-token',
      PLAID_EXCHANGE: '/api/rewardo/plaid/exchange',
      PLAID_SYNC: '/api/rewardo/plaid/sync',
      PLAID_ITEM: (id) => `/api/rewardo/plaid/items/${id}`,
      // Public landing page (/rewardo) — no auth
      PUBLIC_CARDS: '/api/rewardo/public/cards',
      PUBLIC_MERCHANTS: '/api/rewardo/public/merchants',
      PUBLIC_DEMO: '/api/rewardo/public/demo',
      WAITLIST: '/api/rewardo/waitlist',
      WAITLIST_STATS: '/api/rewardo/waitlist/stats',
    },
    CIM_PLAYGROUND: {
      PACKS: '/api/cim-playground/packs',
      CREDITS: '/api/cim-playground/credits',
      CHECKOUT: '/api/cim-playground/checkout',
      CHECKOUT_SESSION: (id) => `/api/cim-playground/checkout/${id}`,
      GENERATE: '/api/cim-playground/generate',
      GENERATE_PDF: '/api/cim-playground/generate-pdf',
      EXTRACT_FROM_NOTES: '/api/cim-playground/extract-from-notes',
      EXTRACT_TEXT_FROM_FILE: '/api/cim-playground/extract-text-from-file',
      SPEND_REFINE: '/api/cim-playground/spend-refine',
    },
    // Aligns with backend routers: /api/advisor, /api/advisors, /api/buyer
    ADVISOR_PROFILE: {
      BASE: '/api/advisor/profile',
      PHOTO: '/api/advisor/profile-photo'
    },
    BUYER_PROFILE: {
      BASE: '/api/buyer/profile',
      STATUS: '/api/buyer/profile/status',
      NETWORK_STATUS: '/api/buyer/network-status',
      NETWORK_SIGNUP: '/api/buyer/network-signup',
      FOR_USER: (buyerUserId) => `/api/buyer/profile/${buyerUserId}`
    },
    // Public Buyer Network signups — backed by routes/buyers_network.py (PostgreSQL).
    BUYER_NETWORK: {
      LEADS: '/api/buyer-leads',
      PLUS_WAITLIST: '/api/buyer-plus-waitlist',
    },
    ADMIN: {
      BUYER_NETWORK: '/api/admin/buyer-network',
      AGENT_METRICS: '/api/admin/agent-metrics',
    },
    BUYER_PORTAL: {
      DASHBOARD: '/api/buyer/dashboard',
      /** Relative to /api/buyer/listings — matches buyer_portal_router prefix */
      listing: (listingId, suffix = '') =>
        suffix ? `/api/buyer/listings/${listingId}/${suffix}` : `/api/buyer/listings/${listingId}`
    },
    DEAL_SOURCING: {
      BUSINESSES: '/api/buyer/sourcing/businesses',
      BUSINESS: (id) => `/api/buyer/sourcing/businesses/${id}`,
      INDUSTRIES: '/api/buyer/sourcing/industries',
      STATES: '/api/buyer/sourcing/states',
      WEBSITE_ANALYSIS: (id) => `/api/buyer/sourcing/businesses/${id}/website-analysis`,
      SITE_ANALYSIS: (id) => `/api/buyer/sourcing/businesses/${id}/site-analysis`,
      AI_INSIGHT: (id) => `/api/buyer/sourcing/businesses/${id}/ai-insight`,
      PREFERENCES: '/api/buyer/sourcing/preferences',
    }
  }
}

// Helper function to get full API URL
export const getApiUrl = (endpoint) => {
  const base = API_CONFIG.BASE_URL || ''
  return `${base}${endpoint}`
}

/**
 * WebSocket URL for backend-proxied Gemini Live (same cookie auth as REST when same-origin).
 * Production: set REACT_APP_BACKEND_URL so WSS targets the API host.
 */
export const getVoiceWebSocketUrl = () => {
  const base = API_CONFIG.BASE_URL || ''
  if (base) {
    try {
      const url = new URL(base.startsWith('http') ? base : `https://${base}`)
      const wsProto = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${wsProto}//${url.host}/api/cim-voice/live`
    } catch {
      /* fall through to same-origin */
    }
  }
  const wsProto = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3000'
  return `${wsProto}//${host}/api/cim-voice/live`
}

// Helper function to make API calls
export const apiCall = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint)
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json' } }
  
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    credentials: options.credentials ?? 'include' }

  try {
    const response = await fetch(url, mergedOptions)
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }
    
    return data
  } catch (error) {
    console.error('API call failed:', error)
    throw error
  }
}

export { API_CONFIG }
export default API_CONFIG
